import { useCallback, useMemo } from 'react';
import { useSettings } from '@wix/tpa-settings/react';
import { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { useOnlineOrdersStorage } from '../../storage';
import { useProcessedMenus } from '../../hooks/useProcessedMenus';
import { useGetLayoutSize } from '../../../../components-shared/LayoutSizeProvider';
import settingsParams from '../../settingsParams';
import { getMenuAvailabilityStatusText } from '../../../../utils/menuAvailability';
import { DEFAULT_TIMEZONE } from '../../../../utils/constants';
import { DispatchType } from '../../../../types/businessTypes';

type MenuAvailabilityNotification = {
  text: string;
  buttonText?: string;
  onButtonClick?: () => void;
};

const menuAvailabilityButtonKeyByDispatchType: Record<DispatchType, string> = {
  [DispatchType.PICKUP]:
    'menu_olo.menuAvailabilityStatus.nextAvailable.pickup.button',
  [DispatchType.DELIVERY]:
    'menu_olo.menuAvailabilityStatus.nextAvailable.delivery.button',
  [DispatchType.DINE_IN]:
    'menu_olo.menuAvailabilityStatus.nextAvailable.dine_in.button',
};

export const useHooks = () => {
  const { t } = useTranslation();
  const { layoutSize } = useGetLayoutSize();
  const {
    currentOperation,
    operations,
    isLocationSelected,
    menuAvailabilityById,
    menuAvailabilityStatusById,
    menuNextAvailabilityById,
    dispatchesInfo,
    selectedDispatchType,
    acceptingDispatchTypesByOperationId,
    openFulfillmentModal,
    siteLocale,
    timezone,
  } = useOnlineOrdersStorage();
  const settings = useSettings();
  const isGhostMode = operations.length > 1 && !isLocationSelected;

  const showMenuDescription = settings.get(settingsParams.showMenuDescription);
  const showMenuDivider = settings.get(settingsParams.showMenuDivider);

  const { processedMenus, itemAvailabilityByPlacementId } = useProcessedMenus();

  const openFulfillmentModalFromMenuBanner = useCallback(() => {
    if (!currentOperation) {
      return;
    }

    openFulfillmentModal({
      operations,
      currentOperation,
      dispatchesInfo,
      selectedDispatchType,
      acceptingDispatchTypesByOperationId,
    });
  }, [
    operations,
    currentOperation,
    dispatchesInfo,
    selectedDispatchType,
    acceptingDispatchTypesByOperationId,
    openFulfillmentModal,
  ]);

  const availabilityNotificationByMenuId = useMemo(() => {
    if (isGhostMode) {
      return {};
    }

    const resolvedTimezone = timezone || DEFAULT_TIMEZONE;

    return processedMenus.reduce<Record<string, MenuAvailabilityNotification>>(
      (acc, menu) => {
        const isAvailable =
          menuAvailabilityStatusById[menu.id] !==
          AvailabilityStatus.UNAVAILABLE;
        const hasNextAvailability = !!menuNextAvailabilityById[menu.id];
        const text = isAvailable
          ? ''
          : getMenuAvailabilityStatusText({
              availability: menuAvailabilityById[menu.id],
              hasNextAvailability,
              locale: siteLocale,
              timezone: resolvedTimezone,
              t,
            });

        if (text) {
          acc[menu.id] = {
            text,
            buttonText:
              hasNextAvailability && selectedDispatchType
                ? t(
                    menuAvailabilityButtonKeyByDispatchType[
                      selectedDispatchType
                    ],
                  )
                : undefined,
            onButtonClick: hasNextAvailability
              ? openFulfillmentModalFromMenuBanner
              : undefined,
          };
        }

        return acc;
      },
      {},
    );
  }, [
    processedMenus,
    isGhostMode,
    menuAvailabilityStatusById,
    menuNextAvailabilityById,
    menuAvailabilityById,
    siteLocale,
    timezone,
    selectedDispatchType,
    openFulfillmentModalFromMenuBanner,
    t,
  ]);

  return {
    menus: processedMenus,
    itemAvailabilityByPlacementId,
    availabilityNotificationByMenuId,
    layoutSize,
    showMenuDescription,
    showMenuDivider,
  };
};
